import { z } from 'zod';

import { BooleanFromString } from '../common';

export const NewsCategorySchema = z.enum(['announcement', 'event', 'promotion', 'milestone']);
export type NewsCategory = z.infer<typeof NewsCategorySchema>;

export const NewsArticleSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  titleId: z.string().optional(),
  excerpt: z.string().nullable(),
  excerptId: z.string().nullable().optional(),
  coverImageUrl: z.string().nullable(),
  category: z.string().nullable(),
  isFeatured: z.boolean(),
  isPublished: z.boolean(),
  publishedAt: z.string().nullable(),
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type NewsArticle = z.infer<typeof NewsArticleSchema>;

export const NewsArticlesResponseSchema = z.object({
  articles: z.array(NewsArticleSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type NewsArticlesResponse = z.infer<typeof NewsArticlesResponseSchema>;

export const NewsQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  category: NewsCategorySchema.optional(),
  isPublished: BooleanFromString.optional(),
  isFeatured: BooleanFromString.optional(),
  search: z.string().optional(),
});
export type NewsQuery = z.infer<typeof NewsQuerySchema>;

export const CreateNewsSchema = z.object({
  title: z.string().min(1).max(300),
  titleId: z.string().max(200).optional(),
  slug: z.string().max(200).optional(),
  excerpt: z.string().max(500).optional(),
  excerptId: z.string().max(500).optional(),
  coverImageUrl: z.string().url().max(500).optional(),
  category: NewsCategorySchema.optional(),
  isFeatured: z.boolean().optional(),
  isPublished: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type CreateNewsInput = z.infer<typeof CreateNewsSchema>;

export const UpdateNewsSchema = CreateNewsSchema.partial();
export type UpdateNewsInput = z.infer<typeof UpdateNewsSchema>;

// ============================================================================
// Admin News — Query Schema (stricter sortBy enum for admin listing)
// ============================================================================

export const AdminNewsQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  sortBy: z.enum(['createdAt', 'publishedAt', 'sortOrder', 'title']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  category: NewsCategorySchema.optional(),
  isPublished: BooleanFromString.optional(),
  isFeatured: BooleanFromString.optional(),
  search: z.string().optional(),
});
export type AdminNewsQuery = z.infer<typeof AdminNewsQuerySchema>;

// ============================================================================
// Admin News — Response Schemas
// ============================================================================

export const DeleteNewsResponseSchema = z.object({
  deleted: z.literal(true),
});
